import { Helmet } from "react-helmet";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
  Tooltip,
  tooltipClasses
} from "@mui/material";
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@mui/styles";
import { styled } from "@mui/material/styles";
import moment from "moment";
import { useTranslation } from "react-i18next";
import cssVariables from "../../../../theme/cssVariables";

import InputForm from "../../../../components/achievement/InputForm";
import AlertDialog from "../../../../components/AlertDialog";
import SimpleDialog from "../../../../components/SimpleDialog";
import ConfirmDialog from "../../../../components/ConfirmDialog";
import MessageDialog from "../../../../components/inbox/MessageDialog";
import CustomPagination from "../../../../components/CustomPagination";

import CloseIcon from "../../../../assets/images/plus-circle-fill.svg";
import fileBrowser from "../../../../icons/file-browser.svg";
import Search from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ReactComponent as PlusIcon } from "../../../../icons/plus.svg";
import { ReactComponent as ChartGreen } from "../../../../assets/images/achievement/chart-green.svg";
import { ReactComponent as FilterIcon } from "../../../../icons/filter.svg";
import { ReactComponent as PdfIcon } from "../../../../assets/images/iconFile/pdf.svg";
import { ReactComponent as JpgIcon } from "../../../../assets/images/iconFile/jpg.svg";
import { ReactComponent as PngIcon } from "../../../../assets/images/iconFile/png.svg";
import { ReactComponent as NullIcon } from "../../../../assets/images/iconFile/Null-File.svg";
import { ReactComponent as TrashIcon } from "../../../../assets/images/portfolio/trash-alt.svg";
import { ReactComponent as PencilIcon } from "../../../../icons/pencil.svg";
import { ReactComponent as NoDataIcon } from "../../../../assets/icons/nodata.svg";

import {
  getKpiFormList,
  getAchievementSummary,
  getAchievementList,
  postAchievementAdd,
  getAchievementEdit,
  postAchievementEdit,
  deleteAchievement,
} from "../../../../actions/achievement";

import ApexCharts from "react-apexcharts";
import * as Util from "../../../../services/global.service";

const BootstrapTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: "#343A40",
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#343A40",
    color: "#FFFFFF",
    padding: "11px 15px",
  },
}));

// main component
const Achievement = (props) => {
  const { t, i18n } = useTranslation();
  const achievement = useSelector((state) => state.Achievement);
  const dispatch = useDispatch();
  const [query] = useSearchParams();
  const [formData, setFormData] = React.useState({
    name: "",
    description: "",
    startDate: new Date(),
    endDate: new Date(),
    metric: "",
    metricCondition: "",
    metricUnit: "",
    metricValue: "",
    isLink: false,
    selectedGoals: [],
    selectedCompetencies: [],
  })

  // kpi form select
  const [kpiFormList, setKpiFormList] = React.useState([])
  const [kpiFormSelect, setKpiFormSelect] = React.useState("")

  const [isOwner, setIsOwner] = React.useState(false)

  const loadKpiForm = () => {
    dispatch(getKpiFormList("achievement", query.get("emp_id"))).then((resp) => {
      setKpiFormList(resp);
      let isDefault = resp.find((obj) => obj.is_default);
      if (Util.formatData(query.get("cycle"), false)) {
        setKpiFormSelect(query.get("cycle"));
      } else if (isDefault) {
        setKpiFormSelect(isDefault.value);
      } else {
        setKpiFormSelect(resp.length > 0 ? resp[0].value : "");
      }
    });
  };

  const loadSummaryAndList = () => {
    if (kpiFormSelect !== "") {
      dispatch(getAchievementSummary(kpiFormSelect, query.get("emp_id"))).then((resp) => {
        setSummaryData(resp?.summary)
      })
      dispatch(getAchievementList(kpiFormSelect, query.get("emp_id"))).then((resp) => {
        setList(resp?.list_achievement)
        setFilteredList(resp?.list_achievement)
        setIsOwner(resp?.is_owner)
      })
    }
  };

  useEffect(() => {
    loadSummaryAndList();
    if (query.get("goal") || query.get("competency")) {
      const goal = query.get("goal");
      const competency = query.get("competency");
      handlePreSelectForm(goal, competency);
    }
  }, [kpiFormSelect]);

  const handlePreSelectForm = (goal, competency) => {
    handleOpenDialog("edit");
    let formatResp = {
      achievementSerial: "",
      name: "",
      description: "",
      startDate: new Date(),
      endDate: Util.subStractDate(new Date(), "add", 1),
      metric: "",
      metricCondition: "",
      metricUnit: "",
      metricValue: "",
      isLink: true,
      selectedGoals: goal !== "" ? [goal] : [],
      selectedCompetencies: competency !== "" ? [competency] : [],
    };
    setFormData(formatResp);
  };

  const setSummaryData = (params) => {
    const data = {
      total: params?.total ?? 0,
      goal: params?.goal ?? 0,
      competency: params?.competency ?? 0,
      goalAndCompetency: params?.goal_and_competency ?? 0,
      others: params?.others ?? 0,
    };
    setSummaryItem(data);
  };

  // summary chart
  let chartOptions = {
    series: [
      {
        name: "Goals",
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      },
      {
        name: "Competencies",
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      },
      {
        name: "GoalsAndCompetencies",
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      },
      {
        name: "Others",
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      },
    ],
    options: {
      chart: {
        type: "bar",
        height: "100px",
        stacked: true,
        toolbar: {
          show: false,
        },
      },
      colors: ["#FA9349", "#74C1ED", "#F7B254", "#31B09B"],
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 5,
          columnWidth: "30%",
        },
      },
      dataLabels: {
        enabled: false,
      },
      grid: {
        show: false,
      },
      stroke: {
        width: 1,
        colors: ["#fff"],
      },
      title: {
        show: false,
      },
      xaxis: {
        categories: [
          "MM-YY",
          "MM-YY",
          "MM-YY",
          "MM-YY",
          "MM-YY",
          "MM-YY",
          "MM-YY",
          "MM-YY",
          "MM-YY",
          "MM-YY",
          "MM-YY",
          "MM-YY",
        ],
        labels: {
          formatter: function (val) {
            return val;
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        title: {
          text: undefined,
        },
        tickAmount: 1,
        labels: {
          formatter: function (val) {
            return val.toFixed(0);
          }
        }
      },
      tooltip: {
        enabled: true,
        enabledOnSeries: undefined,
        shared: true,
        followCursor: false,
        intersect: false,
        inverseOrder: false,
        custom: undefined,
        fillSeriesColor: false,
        style: {
          fontSize: "16px",
          fontFamily: "Kanit",
          fontWeight: 300,
        },
        onDatasetHover: {
          highlightDataSeries: false,
        },
        x: {
          show: true,
          formatter: function (value, opts) {
            let total = opts.w.globals.stackedSeriesTotals[opts.dataPointIndex];
            return `${t("label.total")}: ` + Math.trunc(total);
          },
        },
        y: {
          formatter: undefined,
          title: {
            formatter: (seriesName) => t(`label.${seriesName}`) + ":",
          },
        },
        z: {
          formatter: undefined,
          title: "Size: ",
        },
        marker: {
          show: true,
        },
        items: {
          display: "flex",
        },
        fixed: {
          enabled: false,
          position: "topRight",
          offsetX: 0,
          offsetY: 0,
        },
      },
      fill: {
        opacity: 1,
      },
      legend: {
        show: false,
        position: "top",
        horizontalAlign: "right",
      },
    },
  };
  const [chart, setChart] = React.useState(chartOptions);
  const handleChangeKpiForm = (event) => {
    setKpiFormSelect(event.target.value);
  };
  const [summaryItems, setSummaryItem] = React.useState({
    total: 0,
    goal: 0,
    competency: 0,
    goalAndCompetency: 0,
    others: 0,
  });

  const _ = require("lodash");
  const [filteredList, setFilteredList] = React.useState([]);
  const [list, setList] = React.useState([]);
  const [perPage, setPerPage] = React.useState(10);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPage, setTotalPage] = React.useState(1);


  const mapThaiMonthName = (month) => {
    let result = ""
    switch (month) {
      case "Jan":
        result = "ม.ค."
        break;
      case "Feb":
        result = "ก.พ."
        break;
      case "Mar":
        result = "มี.ค."
        break;
      case "Apr":
        result = "เม.ย."
        break;
      case "May":
        result = "พ.ค."
        break;
      case "Jun":
        result = "มิ.ย."
        break;
      case "Jul":
        result = "ก.ค."
        break;
      case "Aug":
        result = "ส.ค."
        break;
      case "Sep":
        result = "ก.ย."
        break;
      case "Oct":
        result = "ต.ค."
        break;
      case "Nov":
        result = "พ.ย."
        break;
      case "Dec":
        result = "ธ.ค."
        break;
      default:
        break;
    }
    return result
  }

  // on dialog backdrop click
  const backdropClick = (event) => {
    return false;
  };

  const handlePerPageChange = (event) => {
    setPerPage(event.target.value);
  };

  const handleSetCurrentPage = (value) => {
    const getSelectedPage = value.filter(
      (item) => item.type === "page" && item.selected
    );
    setCurrentPage(getSelectedPage.length > 0 ? getSelectedPage[0].page : 1);
  };

  const handleTotalPage = () => {
    const totalPage =
      Math.ceil(filteredList.length / perPage) === 0
        ? 1
        : Math.ceil(filteredList.length / perPage);
    setTotalPage(totalPage);
  };

  const currentTableData = useMemo(() => {
    handleTotalPage();
    const firstPageIndex = (currentPage - 1) * perPage;
    const lastPageIndex = firstPageIndex + perPage;
    return filteredList.slice(firstPageIndex, lastPageIndex);
  }, [filteredList, currentPage, perPage]);

  // dialog
  const [formType, setFormType] = React.useState("");
  const [isAddDialogOpen, openDialog] = React.useState(false);
  const handleOpenDialog = (value) => {
    setFormType(value);
    openDialog(true);
  };

  const [discardDialog, setDiscardDialog] = React.useState(false);
  const handleDiscardDialog = (value) => {
    setDiscardDialog(value);
  };
  const handleDiscardChangeConfirm = () => {
    handleDiscardDialog(false);
    openDialog(false);
    setFormData({
      name: "",
      description: "",
      startDate: new Date(),
      endDate: new Date(),
      metric: "",
      metricCondition: "",
      metricUnit: "",
      metricValue: "",
      isLink: false,
      selectedGoals: [],
      selectedCompetencies: [],
    });
  };
  const compareInputForm = (oldVal, newVal) => {
    const checkEveryElement = (a, b) => {
      return a.every((v, i) => v === b[i]);
    };
    let isEqual = true;
    if (oldVal.description !== newVal.description) isEqual = false;
    if (Util.formatDate(oldVal.endDate) !== newVal.endDate) isEqual = false;
    if (Util.formatDate(oldVal.startDate) !== newVal.startDate) isEqual = false;
    if (oldVal.isLink !== newVal.isLink) isEqual = false;
    if (oldVal.metric !== newVal.metric) isEqual = false;
    if (oldVal.metricCondition !== newVal.metricCondition) isEqual = false;
    if (oldVal.metricUnit !== newVal.metricUnit) isEqual = false;
    if (oldVal.metricValue !== newVal.metricValue) isEqual = false;
    if (oldVal.name !== newVal.name) isEqual = false;
    if (
      oldVal.selectedGoals.length !== newVal.selectedGoals.length &&
      !checkEveryElement(oldVal.selectedGoals, newVal.selectedGoals)
    ) {
      isEqual = false;
    }
    if (
      oldVal.selectedCompetencies.length !==
      newVal.selectedCompetencies.length &&
      !checkEveryElement(
        oldVal.selectedCompetencies,
        newVal.selectedCompetencies
      )
    ) {
      isEqual = false;
    }
    return isEqual;
  };
  const handleCloseDialog = (values = null) => {
    if (values) {
      let isFormEqual = compareInputForm({ ...formData }, values);
      if (isFormEqual) {
        openDialog(false);
        setFormData({
          name: "",
          description: "",
          startDate: new Date(),
          endDate: new Date(),
          metric: "",
          metricCondition: "",
          metricUnit: "",
          metricValue: "",
          isLink: false,
          selectedGoals: [],
          selectedCompetencies: [],
        });
      } else {
        handleDiscardDialog(true);
      }
    } else {
      openDialog(false);
      setFormData({
        name: "",
        description: "",
        startDate: new Date(),
        endDate: new Date(),
        metric: "",
        metricCondition: "",
        metricUnit: "",
        metricValue: "",
        isLink: false,
        selectedGoals: [],
        selectedCompetencies: [],
      });
    }
  };
  // add / edit
  const handleSubmitForm = (values) => {
    if (values?.metricUnit === "custom") {
      values.metricUnit = ""
    }
    values.cycle = kpiFormSelect
    dispatch(postAchievementAdd(values))
      .then((resp) => {
        loadSummaryAndList();
        handleSuccessSubmitDialog();
      })
      .catch(() => {
        seterrorText(t("label.pleaseTryAgain"));
        // handleOpenFailedMessageDialog(true);
        loadSummaryAndList();
      });
  };

  const handleSubmitEditForm = (values) => {
    values.cycle = kpiFormSelect
    dispatch(postAchievementEdit(values, values.achievementSerial))
      .then((resp) => {
        loadSummaryAndList();
        handleSuccessEditDialog();
      })
      .catch(() => {
        seterrorText(t("label.pleaseTryAgain"));
        // handleOpenFailedMessageDialog(true);
        loadSummaryAndList();
      });
  };

  const [isSuccessSubmitDialogOpen, setSuccessSubmitDialog] =
    React.useState(false);
  const [isSuccessEditDialogOpen, setSuccessEditDialog] = React.useState(false);
  const handleSuccessSubmitDialog = () => {
    setSuccessSubmitDialog(true);
    handleCloseDialog();
  };
  const handleSuccessEditDialog = () => {
    setSuccessEditDialog(true);
    handleCloseDialog();
  };

  const [isSuccessDeleteDialogOpen, openSuccessDeleteDialog] =
    React.useState(false);
  const [isSuccessAddDialogOpen, openSuccessAddDialog] = React.useState(false);
  const [isConfirmDeleteDialogOpen, openConfirmDeleteDialog] =
    React.useState(false);
  const [currentAchievementSerial, setCurrentAchievementSerial] =
    React.useState("");
  const [currentSelectedDeleteList, setSelectedDeleteList] = React.useState("");
  const [valueSerialList, setValueSerialList] = React.useState("");

  const [isAlertDialogOpen, openAlertDialogOpen] = React.useState(false);
  const [title, setTitle] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [color, setColor] = React.useState("");
  const [colorHover, setColorHover] = React.useState("");
  const [size, setSize] = React.useState("");
  const [typeAlertDialog, setTypeAlertDialog] = React.useState("");
  const [errorText, seterrorText] = React.useState("");
  const onCloseAlertDialog = () => {
    setTitle("");
    setMessage("");
    setColor("");
    setTypeAlertDialog("");
    setColorHover("");
    setSize("");
    openAlertDialogOpen(false);
  };

  const onOpenAlertDialog = (title, message, color, type, colorHover, size) => {
    setTitle(title);
    setMessage(message);
    setColor(color);
    setColorHover(colorHover);
    setTypeAlertDialog(type);
    setColorHover(colorHover);
    setSize(size);
    openAlertDialogOpen(true);
  };

  const handleConfirmDeleteDialog = (val, serial) => {
    setCurrentAchievementSerial(serial);
    openConfirmDeleteDialog(val);
  };

  const [openFailedMessageDialog, setFailedMessageDialog] =
    React.useState(false);
  const handleOpenFailedMessageDialog = (value) => {
    setFailedMessageDialog(true);
  };
  const handleCloseFailedMessageDialog = (value) => {
    setFailedMessageDialog(false);
  };
  const handleConfirmDeleteList = () => {
    if (currentAchievementSerial !== "") {
      dispatch(deleteAchievement(currentAchievementSerial)).then((resp) => {
        loadSummaryAndList();
        openConfirmDeleteDialog(false);
        openSuccessDeleteDialog(true);
      });
    } else {
      setFailedMessageDialog(true);
    }
  };

  // edit form
  const handleEditForm = (values) => {
    dispatch(getAchievementEdit(values)).then((resp) => {
      let achievement = resp?.kpi_achievement;
      let goalMetric = resp?.goal_metric;
      let goalMetricUnit = resp?.goal_metric_unit;
      let goals = resp.goals.map((item) => {
        return item.kpi_form_goal.serial;
      });
      let competencies = resp.competencies.map((item) => {
        return item.kpi_form_competency.serial;
      });
      let formatResp = {
        achievementSerial: achievement.serial,
        name: achievement.name,
        description: achievement.description,
        startDate: achievement.startAt,
        endDate: achievement.endAt,
        metric: goalMetric.serial,
        metricCondition: achievement.targetCondition
          ? achievement.targetCondition
          : "",
        metricUnit: goalMetricUnit.data === "custom" ? goalMetricUnit.data : goalMetricUnit.data.serial,
        metricUnitCustom: achievement.metricUnitCustom,
        metricValue: achievement.metricValue,
        isLink: achievement.isLink === "y" ? true : false,
        selectedGoals: goals,
        selectedCompetencies: competencies,
      };
      setFormData(formatResp);
      handleOpenDialog("edit");
    });
  };

  // handle search
  const [search, setSearchValue] = React.useState("");
  const [checkbox, setCheckbox] = React.useState([true, true, true, true]);
  const [checkboxStatus, setCheckboxStatus] = React.useState("all");

  const handleSearch = (event) => {
    setSearchValue(event.target.value);
  }


  const handleCheckbox = (data) => {
    let tmp = [...checkbox];
    if (data === "all") {
      if (checkboxStatus === "indeterminate") {
        tmp = tmp.map((item) => (item = true));
      } else if (checkboxStatus === "all") {
        tmp = tmp.map((item) => (item = false));
      } else if (checkboxStatus === "none") {
        tmp = tmp.map((item) => (item = true));
      }
    } else if (data === "goal") {
      tmp[0] = !tmp[0];
    } else if (data === "competency") {
      tmp[1] = !tmp[1];
    } else if (data === "goal_competency") {
      tmp[2] = !tmp[2];
    } else if (data === "others") {
      tmp[3] = !tmp[3];
    }
    setCheckbox(tmp);
  };
  const checkCheckboxStatus = () => {
    let countChecked = 0;
    let result = "none";
    checkbox.forEach((item) => {
      if (item) countChecked++;
    });
    if (countChecked === checkbox.length) {
      result = "all";
    } else if (countChecked > 0 && countChecked < checkbox.length) {
      result = "indeterminate";
    }
    setCheckboxStatus(result);
  };


  // useEffect

  useEffect(() => {
    loadKpiForm()
  }, [])

  // set chart
  useEffect(() => {
    i18n.language === "th-TH" ? moment.locale('th') : moment.locale('en')
    let monthRangeStart = moment()
    let monthRangeEnd = moment()
    let monthDiff = monthRangeEnd.diff(monthRangeStart, 'months')
    let currentKpiForm = kpiFormList.find(form => form.value === kpiFormSelect)
    if (kpiFormList.length > 0) {
      monthRangeStart = currentKpiForm?.start_at ?? false ? moment(currentKpiForm.start_at, 'DD-MM-YYYY') : moment()
      monthRangeEnd = currentKpiForm?.end_at ?? false ? moment(currentKpiForm.end_at, 'DD-MM-YYYY') : moment()
      monthDiff = monthRangeEnd.diff(monthRangeStart, 'months')
    }
    let initSeries = []
    const categories = [];
    let months = [];
    for (let i = 0; i <= monthDiff; i++) {
      initSeries.push(0)
      let date = monthRangeStart
      if (i === 0) {
        date.add(0, "months")
      } else {
        date.add(1, "months")
      }
      let month = date.format("MM");
      let monthName = date.format("MMM");
      let year = date.format("YYYY");

      months.push({
        monthNo: month,
        monthName: monthName,
        year: parseInt(year),
      })
      if (i18n.language === "th-TH") {
        // monthName = mapThaiMonthName(monthName)
        year = parseInt(year) + 543
      }
      categories.push(`${monthName}-${year}`);
    }
    let seriesData = {
      goals: {
        name: "Goals",
        data: [...initSeries],
      },
      competencies: {
        name: "Competencies",
        data: [...initSeries],
      },
      goalsAndCompetencies: {
        name: "GoalsAndCompetencies",
        data: [...initSeries],
      },
      others: {
        name: "Others",
        data: [...initSeries],
      },
    };
    list.forEach((item) => {
      if (item.is_link === "n") {
        months.forEach((month, index) => {
          let current = moment(`${month.year}-${month.monthNo}-01`);
          let startDate = moment(item.start_at);
          let formatStartDate = moment(
            `${startDate.format("YYYY")}-${startDate.format("MM")}-01`
          );
          let endDate = moment(item.end_at);
          let formatEndDateStart = moment(
            `${endDate.format("YYYY")}-${endDate.format("MM")}-01`
          );
          let formatEndDateEnd = moment(
            `${endDate.format("YYYY")}-${endDate.format("MM")}-28`
          );
          // if (moment(current).isBetween(formatStartDate, formatEndDate, undefined, '[]')) {
          //   seriesData.others.data[index]++
          // }
          if (
            moment(current).isBetween(
              formatEndDateStart,
              formatEndDateEnd,
              undefined,
              "[]"
            )
          ) {
            seriesData.others.data[index]++;
          }
        });
      } else {
        if (item.goal === "y" && item.competency === "n") {
          months.forEach((month, index) => {
            let current = moment(`${month.year}-${month.monthNo}-01`);
            let startDate = moment(item.start_at);
            let formatStartDate = moment(
              `${startDate.format("YYYY")}-${startDate.format("MM")}-01`
            );
            let endDate = moment(item.end_at);
            let formatEndDateStart = moment(
              `${endDate.format("YYYY")}-${endDate.format("MM")}-01`
            );
            let formatEndDateEnd = moment(
              `${endDate.format("YYYY")}-${endDate.format("MM")}-28`
            );
            // if (moment(current).isBetween(formatStartDate, formatEndDate, undefined, '[]')) {
            //   seriesData.others.data[index]++
            // }
            if (
              moment(current).isBetween(
                formatEndDateStart,
                formatEndDateEnd,
                undefined,
                "[]"
              )
            ) {
              seriesData.goals.data[index]++;
            }
          });
        } else if (item.goal === "n" && item.competency === "y") {
          months.forEach((month, index) => {
            let current = moment(`${month.year}-${month.monthNo}-01`);
            let startDate = moment(item.start_at);
            let formatStartDate = moment(
              `${startDate.format("YYYY")}-${startDate.format("MM")}-01`
            );
            let endDate = moment(item.end_at);
            let formatEndDateStart = moment(
              `${endDate.format("YYYY")}-${endDate.format("MM")}-01`
            );
            let formatEndDateEnd = moment(
              `${endDate.format("YYYY")}-${endDate.format("MM")}-28`
            );
            // if (moment(current).isBetween(formatStartDate, formatEndDate, undefined, '[]')) {
            //   seriesData.others.data[index]++
            // }
            if (
              moment(current).isBetween(
                formatEndDateStart,
                formatEndDateEnd,
                undefined,
                "[]"
              )
            ) {
              seriesData.competencies.data[index]++;
            }
          });
        } else if (item.goal === "y" && item.competency === "y") {
          months.forEach((month, index) => {
            let current = moment(`${month.year}-${month.monthNo}-01`);
            let startDate = moment(item.start_at);
            let formatStartDate = moment(
              `${startDate.format("YYYY")}-${startDate.format("MM")}-01`
            );
            let endDate = moment(item.end_at);
            let formatEndDateStart = moment(
              `${endDate.format("YYYY")}-${endDate.format("MM")}-01`
            );
            let formatEndDateEnd = moment(
              `${endDate.format("YYYY")}-${endDate.format("MM")}-28`
            );
            if (
              moment(current).isBetween(
                formatEndDateStart,
                formatEndDateEnd,
                undefined,
                "[]"
              )
            ) {
              seriesData.goalsAndCompetencies.data[index]++;
            }
          });
        }
      }
    });
    let tempChartData = {
      ...chart,
      ...{
        series: [
          seriesData.goals,
          seriesData.competencies,
          seriesData.goalsAndCompetencies,
          seriesData.others,
        ],
      },
    };
    tempChartData.options = {
      ...tempChartData.options,
      ...{
        xaxis: {
          ...tempChartData.options.xaxis,
          categories: categories,
        },
      },
    };
    setChart(tempChartData);
  }, [list])

  useEffect(() => {
    let result = [];
    if (search !== "") {
      result = [...list].filter((item) => {
        return Object.keys(item).some((key) => {
          if (item[key]) {
            return item[key].toString().toLowerCase().indexOf(search.toString().toLowerCase()) !== -1;
          }
          return item[key];
        });
      });
    } else {
      result = list;
    }
    let mapCheckbox = {
      goal: checkbox[0],
      competency: checkbox[1],
      goalCompetency: checkbox[2],
      others: checkbox[3],
    };
    let goalList = [],
      competencyList = [],
      goalCompetencyList = [],
      othersList = [];
    if (checkboxStatus === "indeterminate") {
      if (mapCheckbox.goal) {
        goalList = result.filter((item) => {
          if (
            item.is_link === "y" &&
            item.goal === "y" &&
            item.competency === "n"
          ) {
            return item;
          }
          return;
        });
      }
      if (mapCheckbox.competency) {
        competencyList = result.filter((item) => {
          if (
            item.is_link === "y" &&
            item.goal === "n" &&
            item.competency === "y"
          ) {
            return item;
          }
          return;
        });
      }
      if (mapCheckbox.goalCompetency) {
        goalCompetencyList = result.filter((item) => {
          if (
            item.is_link === "y" &&
            item.goal === "y" &&
            item.competency === "y"
          ) {
            return item;
          }
          return;
        });
      }
      if (mapCheckbox.others) {
        othersList = result.filter((item) => {
          if (item.is_link === "n") {
            return item;
          }
          return;
        });
      }
      result = [
        ...goalList,
        ...competencyList,
        ...goalCompetencyList,
        ...othersList,
      ];
    } else if (checkboxStatus === "none") {
      result = [];
    }
    setFilteredList(result);
  }, [search, checkbox, checkboxStatus]);

  useEffect(() => {
    checkCheckboxStatus();
  }, [checkbox])

  // ----------------------------------
  const useStyles = makeStyles({
    root: {
      fontSize: "16px",
    },
    plusSvg: {
      "& > path": {
        fill: "#fff",
        stroke: "#fff",
      },
      searchSvg: {
        "& > path": {
          fill: "#C4C4C4",
        },
      },
    },
    paginationRoot: {
      color: `${cssVariables.color.primary}`,
      backgroundColor: "white!important",
      minWidth: "30px!important",
      padding: "5px!important",
    },
    paginationSelected: {
      color: "white!important",
      border: "1px solid rgba(93, 161, 150, 0.5)!important",
      backgroundColor: `${cssVariables.color.primary}!important`,
      minWidth: "30px!important",
      padding: "5px!important",
    },
    clickable: {
      cursor: "pointer",
      transition:
        "color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      "&:hover": {
        color: "rgb(65, 112, 105) !important",
      },
    },
    fillDisable: {
      "& > path": {
        fill: "#A0A0A0",
      },
      "& g > path": {
        fill: "#A0A0A0",
      },
    },
    btn: {
      "& > svg": {
        marginLeft: "10px",
      },
    },
    achievementItem: {
      cursor: "pointer",
      "&:hover": {
        "-webkit-box-shadow": "0 10px 6px -6px #777",
        "-moz-box-shadow": "0 10px 6px -6px #777",
        boxShadow: "0 10px 6px -6px #777",
      },
    },
  });
  const classes = useStyles();
  const styles = {
    link: {
      color: "#5DA196",
      cursor: "pointer",
    },
    end: {
      color: "#A3A3A3",
    },
  };
  const navigate = useNavigate();
  const acceptConsent = (data) => {
    navigate(data, { replace: false });
  };

  const goToDetail = (url) => {
    navigate(url, { replace: false, state: { formSerial: kpiFormSelect, isOwner: isOwner } });
  };

  const handleAchievementEditClick = React.useCallback((e, data) => {
    e.stopPropagation();
    e.preventDefault();
    handleEditForm(data);
  }, []);

  const handleAchievementDeleteClick = React.useCallback((e, data) => {
    e.stopPropagation();
    e.preventDefault();
    handleConfirmDeleteDialog(true, data);
  }, []);

  const [filtersPopover, seFiltersPopover] = React.useState(null);

  const handleOpenFilters = (event) => {
    seFiltersPopover(event.currentTarget);
  };

  const handleCloseFilters = () => {
    seFiltersPopover(null);
  };

  const filtersPopoverOpen = Boolean(filtersPopover);
  const filtersPopoverId = filtersPopoverOpen ? "simple-popover" : undefined

  const NoData = () => {
    const { t } = useTranslation();
    return (
      <Grid container xs="12" md="12" lg="12" xl="12" display="flex" justifyContent="center" alignItems="center" mt={2}>
        <Grid item display="flex" justifyContent="center" alignItems="center" flexDirection="column">
          <NoDataIcon />
          <Typography fontSize="18px" fontWeight="500" color="#A3A3A3">{t("corporateGoal.label.noData")}</Typography>
        </Grid>
      </Grid>
    )
  }

  return (
    <>
      <Helmet>
        <title>Achievement | PMS </title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: "background.default",
          minHeight: "100%",
          py: 3,
        }}
      >
        <Container maxWidth="xl">
          <Grid container pt={2} pb={2} id="back-to-top-anchor">
            <Typography fontSize={16} lineHeight={1} color="#68717A">
              <span
                style={styles.link}
                onClick={() => acceptConsent("/kpi/home")}
              >
                {t("label.home")}
              </span>{" "}
              / <span style={styles.end}> {t("achievement.label.title")} </span>
            </Typography>
          </Grid>
          <Grid container>
            <Grid item md={12}>
              <Typography
                variant="h1"
                fontSize={36}
                lineHeight="39.6px"
                fontWeight="500"
                color="#404349"
              >
                {t("achievement.label.title")}
              </Typography>
              {kpiFormList?.length > 0 ? (
                <Grid container item my={3}>
                  <Grid item md={6} pr={1}>
                    <Select
                      id="workload-select"
                      inputlabelprops={{ shrink: false }}
                      variant="outlined"
                      size="small"
                      className="white-bg"
                      value={kpiFormSelect}
                      onChange={handleChangeKpiForm}
                    >
                      {kpiFormList?.length > 0 ? (
                        kpiFormList.map((item) => {
                          return (
                            <MenuItem value={item.value} key={item.value}>
                              {item.name}
                            </MenuItem>
                          );
                        })
                      ) : (
                        <MenuItem value="">{t("label.noData")}</MenuItem>
                      )}
                    </Select>
                  </Grid>
                  <Grid
                    item
                    md={6}
                    display="flex"
                    alignItems="flex-end"
                    flexDirection="row-reverse"
                  >
                    <Box>
                      <Typography
                        fontWeight="fontWeightLight"
                        fontSize={12}
                        variant="label"
                      >
                        {t("achievement.label.dataAsOf")} :{" "}
                        {new Date().toLocaleString(
                          i18n.language === "th-TH" ? "th-TH" : "en-GB",
                          {
                            year: "2-digit",
                            month: "short",
                            day: "numeric",
                          }
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              ) : null}
            </Grid>
          </Grid>
          {kpiFormList && kpiFormList?.length > 0 ?
            <>
              {kpiFormList?.length >= 0 ? (
                <Grid container spacing={4}>
                  <Grid item sm md xs={12}>
                    <Card sx={{ maxHeight: "95px" }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid
                            item
                            lg={6}
                            xs={12}
                            display="flex"
                            alignItems="center"
                            textAlign="left"
                          >
                            <Typography
                              fontSize={14}
                              lineHeight="21px"
                              fontWeight="400"
                              color="#6B6B6B"
                            >
                              {t("achievement.label.totalAchievements")}
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            lg={6}
                            xs={12}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Typography
                              fontSize={36}
                              color={summaryItems.total > 0 ? "primary" : "#C4C4C4"}
                              fontWeight="600"
                            >
                              {summaryItems.total}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item sm md xs={12}>
                    <Card sx={{ maxHeight: "95px" }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid
                            item
                            lg={6}
                            xs={12}
                            display="flex"
                            alignItems="center"
                            textAlign="left"
                          >
                            <Typography
                              fontSize={14}
                              lineHeight="21px"
                              fontWeight="400"
                              color="#6B6B6B"
                            >
                              {t("achievement.label.goalAchievements")}
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            lg={6}
                            xs={12}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Typography
                              fontSize={36}
                              color={summaryItems.goal > 0 ? "#FA9349" : "#C4C4C4"}
                              fontWeight="600"
                            >
                              {summaryItems.goal}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item sm md xs={12}>
                    <Card sx={{ maxHeight: "95px" }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid
                            item
                            lg={6}
                            xs={12}
                            display="flex"
                            alignItems="center"
                            textAlign="left"
                          >
                            <Typography
                              fontSize={14}
                              lineHeight="21px"
                              fontWeight="400"
                              color="#6B6B6B"
                            >
                              {t("achievement.label.competencyAchievements")}
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            lg={6}
                            xs={12}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Typography
                              fontSize={36}
                              color={
                                summaryItems.competency > 0 ? "#74C1ED" : "#C4C4C4"
                              }
                              fontWeight="600"
                            >
                              {summaryItems.competency}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item sm md xs={12}>
                    <Card sx={{ maxHeight: "95px" }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid
                            item
                            lg={6}
                            xs={12}
                            display="flex"
                            alignItems="center"
                            textAlign="left"
                          >
                            <Typography
                              fontSize={14}
                              lineHeight="21px"
                              fontWeight="400"
                              color="#6B6B6B"
                            >
                              {t("achievement.label.goalAndCompetencyAchievements")}
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            lg={6}
                            xs={12}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Typography
                              fontSize={36}
                              color={
                                summaryItems.goalAndCompetency > 0
                                  ? "#F7B254"
                                  : "#C4C4C4"
                              }
                              fontWeight="600"
                            >
                              {summaryItems.goalAndCompetency}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item sm md xs={12}>
                    <Card sx={{ maxHeight: "95px" }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid
                            item
                            lg={6}
                            xs={12}
                            display="flex"
                            alignItems="center"
                            textAlign="left"
                          >
                            <pre>
                              <Typography
                                fontSize={14}
                                lineHeight="21px"
                                fontWeight="400"
                                color="#6B6B6B"
                                sx={{ whitespace: "pre" }}
                              >
                                {t("achievement.label.othersAchievements")}
                              </Typography>
                            </pre>
                          </Grid>
                          <Grid
                            item
                            lg={6}
                            xs={12}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Typography
                              fontSize={36}
                              color={
                                summaryItems.others > 0 ? "primary" : "#C4C4C4"
                              }
                              fontWeight="600"
                            >
                              {summaryItems.others}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              ) : null}

              <Grid container spacing={4} mt={1}>
                <Grid item lg={12} md={12}>
                  <Card>
                    <CardContent>
                      <Grid container spacing={1} p={1}>
                        <Grid item lg={12}>
                          <Box display="flex" alignItems="center">
                            <Box mr={3}>
                              <ChartGreen></ChartGreen>
                            </Box>
                            <Typography fontSize={20} fontWeight="500" color="#404349">
                              {t("achievement.label.achievementOverview")}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item lg={12} mt={1}>
                          <Divider />
                        </Grid>
                        <Grid item xs={12} mt={0}>
                          <Box
                            sx={{
                              display: "flex",
                              flexFlow: "row wrap",
                              margin: "0 0 0 auto",
                              maxWidth: "max-content",
                            }}
                          >
                            {chart.series.length > 0
                              ? chart.series.map((loRow, index) => (
                                <Box
                                  key={index}
                                  display="flex"
                                  alignItems="center"
                                >
                                  <Box
                                    sx={{ width: "15px", height: "15px" }}
                                    mr="2.5"
                                  >
                                    <div
                                      className="bar-legend-square"
                                      style={{
                                        backgroundColor:
                                          chart.options.colors[index],
                                      }}
                                    ></div>
                                  </Box>
                                  <Box
                                    sx={{
                                      width: "max-content",
                                      overflow: "hidden",
                                      "white-space": "nowrap",
                                      "text-overflow": "ellipsis",
                                    }}
                                    px="10px"
                                  >
                                    <Typography
                                      variant="label"
                                      style={styles.barLegend}
                                      fontWeight={300}
                                    >
                                      {" "}
                                      {t(`label.${loRow.name}`)}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))
                              : null}
                          </Box>
                          <Box
                            sx={{
                              ".apexcharts-tooltip-marker": { borderRadius: "2px" },
                              ".apexcharts-tooltip-y-group > span": {
                                fontWeight: "400",
                              },
                              ".apexcharts-tooltip": {
                                background: "#F7F7F7",
                                fontWeight: "400",
                              },
                              height: "300px",
                              ".apexcharts-legend-text": {
                                color: "#A3A3A3 !important",
                              },
                              ".apexcharts-text": {
                                color: "#A3A3A3 !important",
                                fill: "#A3A3A3  !important",
                              },
                              ".apexcharts-tooltip-title": {
                                background: "#5DA196 !important",
                                color: "#FFFFFF",
                                fontWeight: "400",
                              },
                            }}
                          >
                            <ApexCharts
                              options={chart.options}
                              series={chart.series}
                              type={chart.options.chart.type}
                              height="100%"
                            ></ApexCharts>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              {kpiFormList?.length >= 0 ? (
                <Grid container mt={4}>
                  <Grid item>
                    <Grid container>
                      <Grid item>
                        <Typography variant="h3" color="#404349">
                          {t("achievement.label.achievementList")}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container>
                      <Grid item>
                        <Typography variant="sub_label">
                          {t("achievement.label.totalAchievements")}: {list.length}{" "}
                          {t("label.items")}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              ) : null}
              <Grid container mt={1.5}>
                <Grid item xs={12} sm={4} md={4} lg={4} mr="auto">
                  <TextField
                    fullWidth
                    id="input-with-icon-textfield"
                    placeholder={t("label.search")}
                    size="small"
                    sx={{ background: "#fff", height: "40px" }}
                    className="searchTextField-h48"
                    value={search}
                    onChange={handleSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search className={classes.searchSvg} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs
                  display="flex"
                  alignItems="flex-start"
                  flexDirection="row-reverse"
                >
                  <Button
                    size="small"
                    aria-controls="simple-menu"
                    aria-haspopup="true"
                    onClick={handleOpenFilters}
                    color="black"
                    variant="outlined"
                    sx={{
                      width: "103px",
                      height: "40px",
                      p: "0",
                      textTransform: "none",
                    }}
                  >
                    <FilterIcon style={{ width: "20px", height: "20px" }} />
                    <Typography ml={1} variant="h6">
                      {t("label.filters")}
                    </Typography>
                  </Button>
                  <Popover
                    id={filtersPopoverId}
                    open={filtersPopoverOpen}
                    anchorEl={filtersPopover}
                    onClose={handleCloseFilters}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                  >
                    {checkbox?.length > 0 ? (
                      <Box
                        sx={{
                          p: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <FormControlLabel
                            label={t("label.selectAll")}
                            control={
                              <Checkbox
                                checked={checkboxStatus === "all"}
                                indeterminate={checkboxStatus === "indeterminate"}
                                onChange={() => handleCheckbox("all")}
                              />
                            }
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <FormControlLabel
                            sx={{ ml: "0!important" }}
                            label={t("achievement.label.goal")}
                            control={
                              <Checkbox
                                color="primary"
                                sx={{ pl: 0 }}
                                checked={checkbox[0]}
                                onChange={() => handleCheckbox("goal")}
                              />
                            }
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <FormControlLabel
                            sx={{ ml: "0!important" }}
                            label={t("achievement.label.competency")}
                            control={
                              <Checkbox
                                color="primary"
                                sx={{ pl: 0 }}
                                checked={checkbox[1]}
                                onChange={() => handleCheckbox("competency")}
                              />
                            }
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <FormControlLabel
                            sx={{ ml: "0!important" }}
                            label={t("achievement.label.goalAndCompetency")}
                            control={
                              <Checkbox
                                color="primary"
                                sx={{ pl: 0 }}
                                checked={checkbox[2]}
                                onChange={() => handleCheckbox("goal_competency")}
                              />
                            }
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <FormControlLabel
                            sx={{ ml: "0!important" }}
                            label={t("achievement.label.others")}
                            control={
                              <Checkbox
                                color="primary"
                                sx={{ pl: 0 }}
                                checked={checkbox[3]}
                                onChange={() => handleCheckbox("others")}
                              />
                            }
                          />
                        </Box>
                      </Box>
                    ) : null}
                  </Popover>
                </Grid>
                {
                  isOwner &&
                  <Grid
                    item
                    xs="auto"
                    sm="auto"
                    md="auto"
                    lg="auto"
                    display="flex"
                    alignItems="flex-start"
                    flexDirection="row-reverse"
                    ml={2}
                  >
                    <Button
                      size="small"
                      aria-controls="simple-menu"
                      aria-haspopup="true"
                      onClick={() => handleOpenDialog("add")}
                      color="primary"
                      variant="contained"
                      sx={{
                        width: "103px",
                        height: "40px",
                        p: "0",
                        textTransform: "none",
                      }}
                    >
                      <PlusIcon className={classes.plusSvg} />
                      <Typography ml={1}>{t("label.add")}</Typography>
                    </Button>
                  </Grid>
                }
              </Grid>
              <Grid container spacing={4} mt={1}>
                {kpiFormList?.length > 0 ? (
                  currentTableData?.length > 0 ? (
                    currentTableData.map((item) => (
                      <Grid item xs={12} sm={12} md={6} lg={6} key={item.serial}>
                        <Card
                          classes={{ root: classes.achievementItem }}
                          onClick={() =>
                            goToDetail(`/kpi/achievement/${item.serial}`)
                          }
                        >
                          <CardContent>
                            <Grid container spacing={5} padding={1}>
                              <Grid item lg={12}>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  width="100%"
                                >
                                  <Box mr={3} sx={{ maxWidth: "70%" }}>
                                    <BootstrapTooltip
                                      placement="top"
                                      arrow
                                      title={item.name}
                                      style={{ marginLeft: '5px' }}
                                    >
                                      <Typography
                                        variant="h4"
                                        sx={{
                                          whiteSpace: "wrap"
                                        }}
                                        style={{ WebkitBoxOrient: 'vertical', WebkitLineClamp: 1, display: '-webkit-box', overflow: 'hidden' }}
                                        color="#404349"
                                      >
                                        {item.name}
                                      </Typography>
                                    </BootstrapTooltip>
                                  </Box>
                                  <Box
                                    sx={{ width: "fit-content" }}
                                    display="flex"
                                    ml="auto"
                                  >
                                    {item.is_link === "y" ? (
                                      <>
                                        <CheckCircleIcon
                                          style={{
                                            fill: cssVariables.color.primary,
                                            marginRight: 3,
                                          }}
                                        ></CheckCircleIcon>
                                        <Typography
                                          color={cssVariables.color.primary}
                                          fontWeight="500"
                                        >
                                          {item.goal === "y" &&
                                            item.competency === "n"
                                            ? t("achievement.label.used.goal")
                                            : item.goal === "n" &&
                                              item.competency === "y"
                                              ? t("achievement.label.used.competency")
                                              : item.goal === "y" &&
                                                item.competency === "y"
                                                ? t(
                                                  "achievement.label.used.goalAndCompetency"
                                                )
                                                : null}
                                        </Typography>
                                      </>
                                    ) : null}
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid
                                item
                                lg={12}
                                sx={{ paddingTop: "20px!important" }}
                              >
                                <Divider />
                              </Grid>
                            </Grid>
                            <Grid container spacing={5} padding={1}>
                              <Grid item lg={12}>
                                <Typography
                                  sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  color="#404349"
                                >
                                  {" "}
                                  {item.description}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Grid container spacing={5} padding={1}>
                              <Grid item lg={12}>
                                <Grid container p={0}>
                                  <Grid item lg={3}>
                                    <Typography variant="sub_label">
                                      {t("achievement.label.metric")}
                                    </Typography>
                                  </Grid>
                                  {item.goal_metric_en === "Value" ? (
                                    <Grid item lg={3}>
                                      <Typography variant="sub_label">
                                        {t("achievement.label.target_condition")}
                                      </Typography>
                                    </Grid>
                                  ) : null}
                                  <Grid item lg={3}>
                                    <Typography variant="sub_label">
                                      {t("achievement.label.value")}
                                    </Typography>
                                  </Grid>
                                  {item.goal_metric_en === "Value" ? (
                                    <Grid item lg={3}>
                                      <Typography variant="sub_label">
                                        {t("achievement.label.unit")}
                                      </Typography>
                                    </Grid>
                                  ) : null}
                                </Grid>
                                <Grid container p={0}>
                                  <Grid item lg={3}>
                                    <Typography
                                      sx={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                      color="#404349"
                                    >
                                      {item.goal_metric_en}
                                    </Typography>
                                  </Grid>
                                  {item.goal_metric_en === "Value" ? (
                                    <Grid item lg={3}>
                                      <Typography
                                        sx={{
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                        color="#404349"
                                      >
                                        {item.target_condition}
                                      </Typography>
                                    </Grid>
                                  ) : null}
                                  <Grid item lg={3}>
                                    <Typography
                                      sx={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                      color="#404349"
                                    >
                                      {item.goal_metric_en === "Accomplishment"
                                        ? Util.capitalizedString(item.metric_value)
                                        : item.goal_metric_en === "Date"
                                          ? Util.formatDate2(item.metric_value)
                                          : item.metric_value}
                                    </Typography>
                                  </Grid>
                                  {item.goal_metric_en === "Value" ? (
                                    <Grid item lg={3}>
                                      <Typography
                                        sx={{
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                        color="#404349"
                                      >
                                        {
                                          item.metric_unit !== ""
                                            ? item.metric_unit
                                            : "N/A"
                                        }
                                      </Typography>
                                    </Grid>
                                  ) : null}
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid container spacing={5} padding={1}>
                              <Grid item lg={12}>
                                <Grid container p={0}>
                                  <Grid item lg={12}>
                                    <Typography variant="sub_label">
                                      {t("achievement.label.attachFiles")}
                                    </Typography>
                                  </Grid>
                                </Grid>
                                <Grid container p={0}>
                                  <Grid item lg={12} display="flex">
                                    <Box sx={{ display: "flex", width: "calc(100% - 60px)", overflow: "hidden" }}>
                                      {
                                        item?.files?.length > 0 ? (
                                          item.files.map((file, index) => (
                                            <>
                                              <Box
                                                sx={{ display: "flex", width: "calc(100% - 100px)" }}
                                                key={file.serial}
                                              >
                                                <React.Fragment>
                                                  <Box
                                                    mr={0.5}
                                                    p={0}
                                                    sx={{
                                                      width: "20px",
                                                      height: "20px",
                                                    }}
                                                  >
                                                    {file.mimetype ===
                                                      "application/pdf" ? (
                                                      <PdfIcon />
                                                    ) : file.mimetype ===
                                                      "image/png" ? (
                                                      <PngIcon />
                                                    ) : file.mimetype ===
                                                      "image/jpeg" ? (
                                                      <JpgIcon />
                                                    ) : (
                                                      <NullIcon />
                                                    )}
                                                  </Box>
                                                  <Typography
                                                    sx={{
                                                      whiteSpace: "nowrap",
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                      maxWidth: "20ch"
                                                    }}
                                                    color="#404349"
                                                  >
                                                    {file.name}
                                                  </Typography>
                                                  {
                                                    index < item.files.length - 1 && (
                                                      <Typography sx={{ mx: 1 }}>
                                                        ,
                                                      </Typography>
                                                    )
                                                  }

                                                </React.Fragment>
                                              </Box>
                                            </>
                                          ))
                                        ) : (
                                          <Typography>-</Typography>
                                        )
                                      }
                                    </Box>
                                    {
                                      item?.files?.length > 3 &&
                                      <Box sx={{ width: "60px" }}>
                                        <Typography>
                                          , more
                                        </Typography>
                                      </Box>
                                    }
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid container p={1}>
                              <Grid item lg={12}>
                                <Divider />
                              </Grid>
                            </Grid>
                            <Grid container px={1}>
                              <Grid
                                item
                                mr="auto"
                                display="flex"
                                alignItems="center"
                                xs
                                sm
                                md
                                lg
                              >
                                <Typography variant="sub_label">
                                  {new Date(item.end_at).toLocaleString(
                                    i18n.language === "th-TH" ? "th-TH" : "en-GB",
                                    {
                                      year: "2-digit",
                                      month: "short",
                                      day: "numeric",
                                    }
                                  )}
                                </Typography>
                              </Grid>
                              {
                                isOwner &&
                                <>
                                  <Grid item ml="auto" display="flex">
                                    <Box ml={1}>
                                      <Button
                                        onClick={(e) => {
                                          handleAchievementEditClick(e, item.serial);
                                        }}
                                        variant="black"
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          textTransform: "none",
                                        }}
                                      >
                                        <Box
                                          mr={1}
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="center"
                                        >
                                          <PencilIcon />
                                        </Box>
                                        <Typography color="#6B6B6B">{t("label.edit")}</Typography>
                                      </Button>
                                    </Box>
                                  </Grid>
                                  <Grid item ml="auto">
                                    <Box ml={1}>
                                      <Button
                                        onClick={(e) => {
                                          handleAchievementDeleteClick(e, item.serial);
                                        }}
                                        variant="black"
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          textTransform: "none",
                                        }}
                                      >
                                        <Box
                                          mr={1}
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="center"
                                        >
                                          <TrashIcon
                                            className="svg-black-stroke"
                                          />
                                        </Box>
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="center"
                                        >
                                          <Typography color="#6B6B6B">{t("label.delete")}</Typography>
                                        </Box>
                                      </Button>
                                    </Box>
                                  </Grid>
                                </>
                              }
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))
                  ) : (
                    <Grid item lg={12} md={12}>
                      <Card>
                        <CardContent>
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            flexDirection="column"
                            p={1}
                          >
                            <Box>
                              <img src={fileBrowser} alt="browser" />
                            </Box>
                            <Box>
                              <Typography variant="sub_label">
                                {t("achievement.label.noData")}
                              </Typography>
                            </Box>
                            {
                              isOwner &&
                              <Box>
                                <Typography
                                  classes={{ root: classes.clickable }}
                                  style={{
                                    color: "#5DA196",
                                    fontWeight: "500",
                                    textDecoration: "underline",
                                  }}
                                  onClick={() => handleOpenDialog("add")}
                                >
                                  {t("achievement.label.clickHereToAdd")}
                                </Typography>
                              </Box>
                            }
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )
                ) : null}
              </Grid>
              {kpiFormList?.length >= 0 ? (
                <Grid container mt={1} spacing={2}>
                  <Grid item xs sm md display="flex" alignItems="center">
                    <Box mr={1}>
                      <Typography variant="s14_fw400">
                        {t("label.showMaxItems")}
                      </Typography>
                    </Box>
                    <Select
                      id="pagination"
                      variant="outlined"
                      defaultValue={10}
                      value={perPage}
                      onChange={handlePerPageChange}
                      displayEmpty
                      size="small"
                      className="white-bg"
                    >
                      <MenuItem value={10}>
                        <Typography style={{ fontWeight: "300" }}>10</Typography>
                      </MenuItem>
                      <MenuItem value={50}>
                        <Typography style={{ fontWeight: "300" }}>50</Typography>
                      </MenuItem>
                      <MenuItem value={100}>
                        <Typography style={{ fontWeight: "300" }}>100</Typography>
                      </MenuItem>
                    </Select>
                  </Grid>
                  <Grid item display="flex" alignItems="center" ml="auto">
                    <Typography variant="s14_fw400">
                      {t("label.page")} {currentPage} {t("label.from")} {totalPage}
                    </Typography>
                  </Grid>
                  <Grid item ml="auto" display="flex" alignItems="center">
                    <CustomPagination
                      setCurrentPage={handleSetCurrentPage}
                      selected={currentPage}
                      count={totalPage}
                    ></CustomPagination>
                  </Grid>
                </Grid>
              ) : null}
            </> : <NoData />
          }
          <Dialog
            onBackdropClick={backdropClick}
            disableEscapeKeyDown
            open={isAddDialogOpen}
            aria-labelledby="form-dialog-title"
            fullWidth={true}
            maxWidth="lg"
            PaperProps={{ sx: { padding: "10px" } }}
          >
            <DialogTitle id="form-add-dialog-title" fontSize={24}>
              {formType !== "edit" ? t("achievement.label.addNewAchievement") : t("achievement.label.editAchievement")}
              {/* <Box
                sx={{
                  position: "absolute",
                  right: 0,
                  margin: "30px 20px 0  0",
                  top: 0,
                }}
                className="clickable"
                onClick={() => handleCloseDialog()}
              >
                <img src={CloseIcon} alt="browser" />
              </Box> */}
            </DialogTitle>
            <DialogContent px={0} pb={0}>
              <Divider />
              <InputForm
                close={handleCloseDialog}
                submit={handleSubmitForm}
                edit={handleSubmitEditForm}
                data={formData}
                type={formType}
                form={kpiFormSelect}
              ></InputForm>
            </DialogContent>
          </Dialog>
          <SimpleDialog
            open={isSuccessSubmitDialogOpen}
            setValue={setSuccessSubmitDialog}
            text={t("achievement.label.dialog.addAchievement")}
          />
          <SimpleDialog
            open={isSuccessEditDialogOpen}
            setValue={setSuccessEditDialog}
            text={t("achievement.label.dialog.editAchievement")}
          />
          <ConfirmDialog
            open={isConfirmDeleteDialogOpen}
            trash={true}
            doConfirm={handleConfirmDeleteList}
            setValue={handleConfirmDeleteDialog}
            title={t("achievement.label.dialog.confirmDeleteTitle")}
            text={t("achievement.label.dialog.confirmDeleteText")}
          />
          <AlertDialog
            open={isAlertDialogOpen}
            title={title}
            message={message}
            type={typeAlertDialog}
            color={color}
            close={onCloseAlertDialog}
            acceptConfirm=""
            colorHover={colorHover}
            size={size}
          />
          <SimpleDialog
            open={isSuccessDeleteDialogOpen}
            setValue={openSuccessDeleteDialog}
            text={t("achievement.label.dialog.deleteAchievement")}
          />
          <SimpleDialog
            open={isSuccessAddDialogOpen}
            setValue={openSuccessAddDialog}
            text={t("achievement.label.dialog.addAchievement")}
          />
          <MessageDialog
            open={openFailedMessageDialog}
            setValue={handleCloseFailedMessageDialog}
            type="fail"
            title={t("achievement.label.dialog.failed")}
            text={errorText}
          />
          <ConfirmDialog
            open={discardDialog}
            warning={true}
            doConfirm={() => handleDiscardChangeConfirm()}
            setValue={() => handleDiscardDialog(false)}
            title={t("achievement.label.dialog.discardTitle")}
            text={t("achievement.label.dialog.discardText")}
          />
        </Container>
        {Util.ScrollToTop()}
      </Box>
    </>
  );
};
export default Achievement;
